/**
 * WEB-3739 юнит 1 — общий список пунктов меню оболочки кабинета нового вида:
 * чистые функции, без React. Ключи и группы — эталон
 * `design/cabinet-rescin/app.jsx` (`menu`, `ICON`), адреса — нынешние адреса
 * кабинета (`Sidebar.tsx`, ИИ-студия — `/ai-studio`), значки — имена `Icon`
 * кита, подписи — ключи словаря `common`. Список читают палитра ⌘K (этот юнит)
 * и новое боковое меню (юнит 2).
 *
 * Правила показа — как в нынешнем меню: у проекта со своими платежами раздел
 * контента подписан «Квизы» (WEB-2126), а «Подписки» с WEB-4035 видны всем —
 * там вкладка «Лиды»; гость
 * организации видит только «Проекты» (WEB-761). «Дашборд» проекта (`home`,
 * `/home`) — первым пунктом всегда: с WEB-3748 юнит 3 это посадка проекта
 * (до него — только при флаге черновика). Признак `draft` прячет на бою
 * «Email-цепочки» (WEB-3768, решение владельца 25.10 п. 79): раздел
 * недопроектирован, адреса писем без флага уводят на главную (`router/DraftRoute`).
 */
import type { IconName } from '@/components/kit/ds/icons';
import type { commonStrings } from '@/i18n/dictionaries/common';

type Common = (typeof commonStrings)['en'];

export type ShellMenuKey =
  | 'home'
  | 'content'
  | 'ai'
  | 'ab'
  | 'analytics'
  | 'email'
  | 'store'
  | 'domains'
  | 'audit'
  | 'settings'
  | 'dash'
  | 'projects'
  | 'balance';

export type ShellLabelKey =
  | 'navDashboard'
  | 'navProjects'
  | 'navBalance'
  | 'navDomains'
  | 'navAudit'
  | 'navFunnels'
  | 'navQuizzes'
  | 'navAiStudio'
  | 'navAbTests'
  | 'navAnalytics'
  | 'navEmailSequences'
  | 'navCommerce'
  | 'navSettings';

export interface ShellMenuItem {
  key: ShellMenuKey;
  path: string;
  icon: IconName;
  labelKey: ShellLabelKey;
}

type Groups = ShellMenuItem[][];

const P: Record<
  Exclude<ShellMenuKey, 'dash' | 'projects' | 'balance'>,
  ShellMenuItem
> = {
  home: { key: 'home', path: '/home', icon: 'home', labelKey: 'navDashboard' },
  content: {
    key: 'content',
    path: '/content',
    icon: 'funnel',
    labelKey: 'navFunnels',
  },
  ai: { key: 'ai', path: '/ai-studio', icon: 'ai', labelKey: 'navAiStudio' },
  ab: { key: 'ab', path: '/ab-tests', icon: 'ab', labelKey: 'navAbTests' },
  analytics: {
    key: 'analytics',
    path: '/analytics',
    icon: 'chart',
    labelKey: 'navAnalytics',
  },
  email: {
    key: 'email',
    path: '/email-sequences',
    icon: 'mail',
    labelKey: 'navEmailSequences',
  },
  store: {
    key: 'store',
    path: '/commerce',
    icon: 'store',
    labelKey: 'navCommerce',
  },
  domains: {
    key: 'domains',
    path: '/domains',
    icon: 'globe',
    labelKey: 'navDomains',
  },
  audit: { key: 'audit', path: '/audit', icon: 'audit', labelKey: 'navAudit' },
  settings: {
    key: 'settings',
    path: '/project/settings',
    icon: 'settings',
    labelKey: 'navSettings',
  },
};

const ORG = {
  dash: {
    key: 'dash',
    path: '/org/dashboard',
    icon: 'chart',
    labelKey: 'navDashboard',
  },
  projects: {
    key: 'projects',
    path: '/projects',
    icon: 'stack',
    labelKey: 'navProjects',
  },
  balance: {
    key: 'balance',
    path: '/org/balance',
    icon: 'wallet',
    labelKey: 'navBalance',
  },
  domains: {
    key: 'domains',
    path: '/org/domains',
    icon: 'globe',
    labelKey: 'navDomains',
  },
  audit: {
    key: 'audit',
    path: '/org/audit',
    icon: 'audit',
    labelKey: 'navAudit',
  },
} as const satisfies Record<string, ShellMenuItem>;

/** Пункты проекта группами эталона: разделы, затем домены, аудит, настройки. */
export function projectShellMenu(o: {
  ownMoney: boolean;
  /**
   * Флаг черновика: без него нет пункта «Email-цепочки» (WEB-3768). Вызов без
   * настоящего флага прячет письма и на стенде — передавать `useDraftEnabled()`.
   */
  draft?: boolean;
}): Groups {
  const content: ShellMenuItem = o.ownMoney
    ? { ...P.content, labelKey: 'navQuizzes' }
    : P.content;
  const main = [
    P.home,
    content,
    P.ai,
    P.ab,
    P.analytics,
    // WEB-3768 (владелец 25.10 п. 79, 27.10): раздел писем недопроектирован —
    // на бою скрыт, страницы и словари на месте, при черновике — как раньше.
    ...(o.draft ? [P.email] : []),
    // WEB-4035 (владелец 29.10): лиды — вкладка «Подписки» → «Лиды», пункт
    // нужен и проекту со своими платежами (внутри у него одна «Лиды»).
    P.store,
  ];
  return [main, [P.domains, P.audit, P.settings]];
}

/** Пункты организации; гостю — только «Проекты». */
export function orgShellMenu(o: { guest: boolean }): Groups {
  if (o.guest) return [[ORG.projects]];
  return [
    [ORG.dash, ORG.projects, ORG.balance],
    [ORG.domains, ORG.audit],
  ];
}

/** Пункты одним списком (для палитры). */
export function shellFlat(groups: Groups): ShellMenuItem[] {
  return groups.flat();
}

/** Подпись пункта на языке кабинета. */
export function shellLabel(item: ShellMenuItem, s: Common): string {
  return s[item.labelKey];
}
